<template>
  <section class="product-search">
    <div class="product-filters">
      <div class="product-filter">
        <button
          class="product-filter__button"
          :class="{'product-filter__button--active' : categoryOpen}"
          :disabled="!categoryItemsFound"
          @click="toggleCategory()"
        >
          <span class="product-filter__button__selection">
            {{ selectedTitle }}
          </span>
        </button>

        <div
          v-if="categoryOpen"
          class="product-filter__dropdown"
        >
          <div class="product-filter__dropdown-section">
            <ul class="product-filter__options">
              <li class="product-filter__option">
                <label class="product-filter__selection">
                  <input
                    v-model="selectedCategory"
                    class="product-filter__option__input"
                    type="radio"
                    :value="false"
                    @change="selectCategory()"
                  >
                  <span class="product-filter__option__value product-filter__option__value--radio">{{ mainCategory.title }}</span>
                  <span class="product-filter__option__count">{{ mainCategory.count }}</span>
                </label>
              </li>
              <li
                v-for="(category, index) in categories"
                v-show="category.count > 0"
                :key="index"
                class="product-filter__option"
              >
                <label class="product-filter__selection">
                  <input
                    v-model="selectedCategory"
                    class="product-filter__option__input"
                    type="radio"
                    :value="category.slug"
                    @change="selectCategory()"
                    @click="toggleCategory()"
                  >
                  <span class="product-filter__option__value product-filter__option__value--radio">{{ category.title }}</span>
                  <span class="product-filter__option__count">{{ category.count }}</span>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="product-filter">
        <ProductFilters
          ref="filters"
          :fixed-brand="fixedBrand"
          :price-range-interval="priceRangeInterval"
          @toggle="updateFilterToggleStatus"
          @filters-changed="filtersChanged"
        />
      </div>

      <div class="product-filter">
        <button
          class="product-filter__button"
          :class="{'product-filter__button--active' : sortOrderOpen}"
          @click="toggleOrder()"
        >
          {{ $modeoL10n.order_verb }}
          <span
            v-if="sortOrder.orderBy !== 'default'"
            class="product-filter__button__value"
          >
            {{ sortOrder.label }}
          </span>
        </button>

        <div
          v-if="sortOrderOpen"
          class="product-filter__dropdown"
        >
          <div class="product-filter__dropdown-section">
            <ul class="product-filter__options">
              <li
                v-for="(sortOrderOption, index) in sortOrderOptions"
                :key="index"
                class="product-filter__option"
              >
                <label class="product-filter__selection">
                  <input
                    v-model="sortOrder"
                    class="product-filter__option__input"
                    type="radio"
                    :value="sortOrderOption"
                    @change="selectOrder()"
                    @click="toggleOrder()"
                  >
                  <span class="product-filter__option__value product-filter__option__value--radio">{{ sortOrderOption.label }}</span>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div
      class="product-search__results"
      :class="{ 'product-search__results--inactive': dropdownsOpen }"
      @click="closeAllFilters()"
    >
      <div class="product-search__details">
        <div
          v-if="!fixedOutlet"
          class="product-special-filters"
        >
          <label
            v-if="productsOnSaleCount > 0"
            class="product-special-filter"
          >
            <input
              v-model="filters.sale"
              class="checkbox-input"
              type="checkbox"
              @change="selectSaleFilter()"
            >
            <span class="product-special-filter__label checkbox-label">{{ $modeoL10n.sale }}</span>
            <span class="product-special-filter__count">{{ productsOnSaleCount }}</span>
          </label>
          <label
            v-if="productsOnStockCount > 0"
            class="product-special-filter"
          >
            <input
              v-model="filters.stock"
              class="checkbox-input"
              type="checkbox"
              @change="selectInStockFilter()"
            >
            <span class="product-special-filter__label checkbox-label">{{ $modeoL10n.in_stock }}</span>
            <span class="product-special-filter__count">{{ productsOnStockCount }}</span>
          </label>
          <label
            v-if="productsOnShowroomCount > 0 && !fixedShowroom"
            class="product-special-filter"
          >
            <input
              v-model="filters.showroom"
              class="checkbox-input"
              type="checkbox"
              @change="selectShowroomFilter()"
            >
            <span class="product-special-filter__label checkbox-label">{{ $modeoL10n.in_showroom }}</span>
            <span class="product-special-filter__count">{{ productsOnShowroomCount }}</span>
          </label>
        </div>

        <div class="product-search__count">
          {{ productCount == 1 ? $modeoL10n.one_product_in_total : $modeoL10n.x_products_in_total | formatString(productCount) }}
        </div>
      </div>

      <div class="product-list">
        <div
          v-for="product in products"
          :key="product.id"
          class="product-list__item"
        >
          <ProductTeaser
            :key="product.id"
            :product="product"
            :type="'grid'"
          />
        </div>
        <template v-if="loading">
          <div
            v-for="n in resultsPerPage"
            :key="n"
            class="product-list__item"
          >
            <ProductContentLoader />
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="currentPage < totalPages"
      class="product-search__footer"
    >
      <button
        class="btn btn--block-small btn--secondary btn--secondary-more"
        :disabled="loading"
        @click="loadMore()"
      >
        {{ $modeoL10n.show_more }}
      </button>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import parser from 'query-string-parser';
import ProductTeaser from './product-teaser.vue';
import ProductFilters from './product-filters.vue';
import ProductContentLoader from './product-content-loader.vue';
import _ from 'lodash';

export default {
    components: {
        ProductTeaser,
        ProductFilters,
        ProductContentLoader,
    },
    props: {
        fixedCategory: {
            type: Object,
            default: () => {
            },
        },
        fixedBrand: {
            type: Object,
            default: () => {
            },
        },
        fixedShowroom: {
            type: Boolean,
            default: () => false,
        },
        fixedOutlet: {
            type: Boolean,
            default: () => false,
        },
        language: {
            type: String,
            default: () => 'fi',
        },
        nonce: {
            type: String,
            default: () => '',
        },
    },
    data : function () {
        return {
            mainCategoryPostCount: 0,
            loading: true,
            categories: [
            ],
            products: [
            ],
            productsOnShowroomCount: 0,
            productsOnSaleCount: 0,
            productsOnStockCount: 0,
            productCount: 0,
            filters: {
                sale: false,
                showroom: false,
                stock: false,
                priceRange: [],
                brands: this.fixedBrand ? [this.fixedBrand.slug] : [],
            },
            brands: [],
            selectedCategory: false,
            sortOrder: {
                orderBy: 'default',
                order: 'ASC',
                label: this.$modeoL10n.default_order,
            },
            sortOrderOptions: [
                {
                    orderBy: 'default',
                    order: 'ASC',
                    label: this.$modeoL10n.default_order,
                },
                {
                    orderBy: 'title',
                    order: 'ASC',
                    label: this.$modeoL10n.name_ascending,
                },
                {
                    orderBy: 'title',
                    order: 'DESC',
                    label: this.$modeoL10n.name_descending,
                },
                {
                    orderBy: 'price',
                    order: 'ASC',
                    label: this.$modeoL10n.lowest_price,
                },
                {
                    orderBy: 'price',
                    order: 'DESC',
                    label: this.$modeoL10n.highest_price,
                },
            ],
            categoryOpen: false,
            sortOrderOpen: false,
            filtersOpen: false,
            priceRange: [],
            priceRangeInterval: 50,
            resultsPerPage: 60,
            currentPage: 1,
            totalPages: null,
        };
    },

    computed: {
        dropdownsOpen: function() {
            if (this.categoryOpen || this.sortOrderOpen || this.filtersOpen) {
                return true;
            } else {
                return false;
            }
        },

        mainCategory: function() {
            if (this.fixedCategory) {
                let myFixedCategory = _.clone(this.fixedCategory);
                myFixedCategory.count = this.mainCategoryPostCount;
                myFixedCategory.title = this.$modeoL10n.all + ' ' + this.fixedCategory.title.toLowerCase();
                if (myFixedCategory.slug == 'outlet') {
                    myFixedCategory.title += this.$modeoL10n.en == 'fi' ? ' products' : '-tuotteet';
                }
                return myFixedCategory;
            } else if(this.fixedBrand) {
                return {placeholder: true, title: this.$modeoL10n.all + ' ' + this.fixedBrand.title + ' ' + this.$modeoL10n.products, count: this.mainCategoryPostCount};
            } else {
                return {placeholder: true, title: this.$modeoL10n.all_products, count: this.mainCategoryPostCount};
            }
        },

        categoryItemsFound() {
            return _.sumBy(_.values(this.categories), cat => { return cat.count; }) > 0;
        },

        selectedCategoryObject() {
          return this.findProductCategoryBySlug(this.selectedCategory);
        },

        selectedTitle() {
          return this.selectedCategory === false || _.isEmpty(this.categories) ? this.mainCategory.title : this.selectedCategoryObject.title
        },
    },

    mounted: function () {
        this.updateResultsPerPageByWindowWidth();
        this.loadStateFromUrl();

        if (this.currentPage > 1) {
            const productCountInFirstLoad = this.currentPage *  this.resultsPerPage;
            //first load, if page is larger than 1
            this.update({updateFilters: true, resultsPerPage: productCountInFirstLoad, page: 1});
        } else {
            this.update({updateFilters: true});
        }

        let component = this;

        jQuery( window ).off('resize');
        jQuery( window ).resize(function() {

            let updated = component.updateResultsPerPageByWindowWidth();

            if (updated) {
                component.dRefetch();
            }

        });

    },

    methods: {

        update(functionParams) {

            let params = this.getParams('backend', {resultsPerPage: functionParams.resultsPerPage, page: functionParams.page});
            this.loading = true;
            let config = {};

            if (this.nonce !== '') {
              config = {
                headers: {
                  'X-WP-Nonce': this.nonce,
                },
              }
            }

            let url = '/wp-json/modeo/v2/products/get?' + params;
            axios.get(url, config)
            .then(response => {

                if (typeof functionParams !== 'undefined' && functionParams.loadMore) {
                    this.products = this.products.concat(response.data.products);
                } else {
                    this.products = response.data.products;
                }

                this.productCount = response.data.foundPosts;
                this.productsOnSaleCount = response.data.sale;
                this.productsOnStockCount = response.data.stock;
                this.productsOnShowroomCount = response.data.showroom;
                if (response.data.brands) {
                    this.brands = _.values(response.data.brands.siblingCats);
                }

                if (response.data.prices && response.data.prices.max) {
                    this.priceRange = [
                        0, Math.ceil(response.data.prices.max / this.priceRangeInterval) * this.priceRangeInterval,
                    ];
                }

                if (this.fixedOutlet) {
                    this.mainCategoryPostCount = response.data.productCats.currentCat[this.fixedCategory.id].count;
                } else if (this.fixedBrand || this.fixedShowroom) {
                    if (response.data.foundPosts == 0) {
                        this.categories = [];
                    } else if (response.data.productCats.parentCats) {
                        this.categories = response.data.productCats.parentCats;
                        this.mainCategoryPostCount = this.totalCount(response.data.productCats.parentCats);
                    }
                } else { //Case fixed category
                    let selectedCategoryId = response.data.productCats.id;
                    if (selectedCategoryId == this.fixedCategory.id && (_.isEmpty(response.data.productCats.currentCat[selectedCategoryId].children) || response.data.productCats.currentCat[selectedCategoryId].children.length == 0) && response.data.productCats.parent) {
                        // Case: Fixed child category with no children.
                        let rootCategoryID = response.data.productCats.parent;
                        this.categories = [];
                        this.mainCategoryPostCount = response.data.productCats.filteredCats[rootCategoryID].children[selectedCategoryId].count;
                    } else if (!response.data.productCats.parent) {
                        let rootCategoryID = response.data.productCats.id;
                        this.categories = response.data.productCats.filteredCats[rootCategoryID].children;
                        this.mainCategoryPostCount = response.data.productCats.filteredCats[rootCategoryID].count;
                    } else {
                        let rootCategoryID = response.data.productCats.parent;
                        this.categories = response.data.productCats.parentCats[rootCategoryID].children;
                        this.mainCategoryPostCount = response.data.productCats.parentCats[rootCategoryID].count;
                    }

                }

                if (!this.fixedOutlet) {
                    this.categories = this.removeOutlet(this.categories);
                }

                this.totalPages = response.data.pages;
                this.currentPage = response.data.page;
                this.loading = false;

                const filters = {
                  productCount: this.productCount,
                  priceRange: this.priceRange,
                  brands: this.brands,
                };
                /* Update Brand and Price filters */
                if (functionParams.updateFilters) {
                    filters.selectedBrands =  this.filters.brands;
                    filters.selectedPriceRange = this.filters.priceRange;
                }
                this.$refs.filters.updateFilters(filters);

            })
            .catch(e => {
                this.loading = false; //Let's end loading animation, if error occurs
                console.error("Error: ", e);
            });
        },

        loadStateFromUrl() {
            const params = parser.fromQuery(location.search);
            if (params) {
                const currentPage = params.page;
                this.currentPage = currentPage;

                if (params.brands) {
                    // Force to Array, because at least Chrome sometimes randomly changes [] to [0],
                    // when encoding url which will be resolved as Object
                    this.filters.brands = this.forceToArrayIfObject(params.brands);
                }

                if (params.priceFrom || params.priceTo) {
                    this.filters.priceRange = [0, 0];

                    if (params.priceFrom) {
                        this.filters.priceRange[0] = params.priceFrom;
                    }

                    if (params.priceTo) {
                        this.filters.priceRange[1] = params.priceTo;
                    }

                }

                if (params.order && params.orderby) {
                    const sortOrderOption = this.findSortOrderObject(params.orderby, params.order);
                    if (sortOrderOption) {
                        this.sortOrder = sortOrderOption;
                    }
                }

                if (params.productCat && (!this.fixedCategory || this.fixedCategory && params.productCat !== this.fixedCategory.slug)) {
                    this.selectedCategory = params.productCat;
                }

                if (params.filters) {
                    let filters = this.forceToArrayIfObject(params.filters);

                    if (filters.indexOf('sale') !== -1) {
                        this.filters.sale = true;
                    }

                    if (filters.indexOf('stock') !== -1) {
                        this.filters.stock = true;
                    }

                    if (filters.indexOf('showroom') !== -1) {
                        this.filters.showroom = true;
                    }
                }
            }

        },

        forceToArrayIfObject(maybeObject) {
            if (typeof maybeObject === 'object') {
                let surelyArray = _.values(maybeObject);
                return surelyArray;
            }
            return maybeObject;
        },

        findSortOrderObject(orderBy, order) {
            return this.sortOrderOptions.filter(orderOption => {
                return (orderOption.order === order && orderOption.orderBy === orderBy)
            })[0];
        },

        selectCategory() {
            this.updateUrlState();
            this.refetch();
        },

        selectOrder() {
            this.updateUrlState();
            this.refetch();
        },

        selectSaleFilter() {
            this.updateUrlState();
            this.refetch();
        },

        selectInStockFilter() {
            this.updateUrlState();
            this.refetch();
        },

        selectShowroomFilter() {
            this.updateUrlState();
            this.refetch();
        },

        findProductCategoryBySlug(slug) {
            const category = _.values(this.categories).filter((category) => {
                return category.slug === slug
            });

            if (category.length > 0) {
                return category[0];
            }
        },

        refetch() {
            this.currentPage = 1;
            this.products = [];
            this.update({});
        },

        dRefetch: _.debounce(function() {
            this.refetch();
        }, 500),


        /* Returns true, if resultsPerPage got updated */
        updateResultsPerPageByWindowWidth() {
            const steps = [
                {columns: 2, rows: 15, width: 514},
                {columns: 3, rows: 15, width: 784},
                {columns: 4, rows: 12, width: 1014},
                {columns: 5, rows: 9, width: 1264},
                {columns: 6, rows: 9, width: 1564},
                {columns: 7, rows: 9, width: 1764},
                {columns: 8, rows: 9, width: 2014},
            ];

            let rows = 10;
            let columns = 1;
            let width = window.innerWidth;

            for (let i=0; i < steps.length; i++) {
                if (width > steps[i].width) {
                    columns = steps[i].columns;
                    rows = steps[i].rows;
                }
            }

            if (this.resultsPerPage !== columns * rows) {
                this.resultsPerPage = columns * rows;
                return true;
            }
            return false;
        },

        getParams(context, forcedParams) {
            let params = '';

            if (!_.isEmpty(this.fixedCategory) && !this.selectedCategory) {
                params += '&productCat=' + this.fixedCategory.slug;
            } else if(!_.isEmpty(this.selectedCategory)) {
                params += '&productCat=' + this.selectedCategory;
            }

            if (this.filters.sale) {
                params += '&filters[]=sale';
            }

            if (this.filters.stock) {
                params += '&filters[]=stock';
            }

            if (this.filters.showroom || this.fixedShowroom) {
                params += '&filters[]=showroom';
            }

            if (this.filters.brands) {
                for (let i=0; i < this.filters.brands.length; i++) {
                    if (typeof this.filters.brands[i] !== 'undefined') {
                        params += '&brands[]=' + this.filters.brands[i]
                    }
                }
            }

            if (this.filters.priceRange && typeof this.filters.priceRange[0] !== 'undefined' && this.filters.priceRange[0] > 0) {
                params += '&priceFrom=' + this.filters.priceRange[0];
            }

            if (this.filters.priceRange && typeof this.filters.priceRange[1] !== 'undefined') {
                params += '&priceTo=' + this.filters.priceRange[1];
            }

            if (this.sortOrder.orderBy) {
                params += '&orderby=' + this.sortOrder.orderBy;
            }

            if (this.sortOrder.order) {
                params += '&order=' + this.sortOrder.order;
            }

            if (context == 'backend' && forcedParams && forcedParams.resultsPerPage) {
                params += '&results=' + forcedParams.resultsPerPage;
            } else if(context == 'backend' && this.resultsPerPage) {
                params += '&results=' + this.resultsPerPage;
            }

            if (forcedParams && forcedParams.page) {
                params += '&page=' + forcedParams.page;
            } else if (this.currentPage) {
                params += '&page=' + this.currentPage;
            }

            params += '&lang=' + this.language;

            return params;
        },

        updateUrlState() {
            let params = this.getParams();
            params = params.replace('&', '?');
            window.history.replaceState( {} , 'params', params);
        },

        toggleCategory() {
            if (this.categoryItemsFound) {
                if (!this.categoryOpen) {
                    this.categoryOpen = true;
                    this.sortOrderOpen = false;
                    this.$refs.filters.close();
                } else {
                    setTimeout(() => {
                        this.categoryOpen = false;
                        this.sortOrderOpen = false;
                        this.$refs.filters.close();
                    }, 50);
                }
            }
        },

        toggleOrder() {
            if (!this.sortOrderOpen) {
                this.sortOrderOpen = true;
                this.categoryOpen = false;
                this.$refs.filters.close();
            } else {
                setTimeout(() => {
                    this.sortOrderOpen = false;
                    this.categoryOpen = false;
                    this.$refs.filters.close();
                }, 50);
            }
        },

        updateFilterToggleStatus(toggleValue) {
            this.filtersOpen = toggleValue;
            if (this.filtersOpen) {
                this.categoryOpen = false;
                this.sortOrderOpen = false;
            }
        },

        closeAllFilters() {
            if (this.dropdownsOpen) {
                this.categoryOpen = false;
                this.sortOrderOpen = false;
                this.$refs.filters.close();
            }
        },


        filtersChanged(selectedFilters) {
            this.filters.priceRange = selectedFilters.priceRange;
            this.filters.brands = selectedFilters.brands;
            this.updateUrlState();
            this.dRefetch();
        },

        loadMore() {
            this.currentPage = this.currentPage + 1;
            this.updateUrlState();
            this.update({'loadMore': true});
        },

        totalCount(someArray) {
            let total = 0;
            let values = _.values(someArray);
            for (let i=0; i < values.length; i++) {
                total = total + values[i].count;
            }
            return total;
        },

        removeOutlet(categories) {
            let keys = Object.keys(categories);
            for (let i = 0; i < keys.length; i++) {
                if (categories[keys[i]].slug == 'outlet') {
                    delete categories[keys[i]]
                }
            }
            return categories;
        },

    },

}
</script>
